import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { Select, SelectItem } from "@/components/generic/Select";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { P, match } from "ts-pattern";
import { z } from "zod";

const schema = z.object({
  value: z.enum(["true", "false"]).nullable(),
});

export function LabelFilter({
  group: { values, ...props },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  {
    values: string[];
    yesLabel: string;
    noLabel: string;
  }
>) {
  const listContainsFilter = queryFilters.value;

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      value: match(listContainsFilter?.operator)
        .with("ListContains", () => "true" as const) // Legacy support for old filter
        .with("HasLabel", () => "true" as const)
        .with("ListDoesNotContain", () => "false" as const) // Legacy support for old filter
        .with("DoesNotHaveLabel", () => "false" as const)
        .otherwise(() => null),
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const submit = handleSubmit((data) => {
    const formattedFilter = match(data.value)
      .with(P.nullish, () => undefined)
      .with("true", () => ({
        operator: "HasLabel" as const,
        values: [...values],
      }))
      .with("false", () => ({
        operator: "DoesNotHaveLabel" as const,
        values: [...values],
      }))
      .exhaustive();

    update({ value: formattedFilter });
  });

  return (
    <Form
      className="flex size-full flex-col items-center justify-between gap-y-12"
      id={formId}
      onSubmit={submit}
    >
      <div className="flex flex-col items-center gap-y-9">
        <SegmentHeader title={props.title} description={props.description} />
        <Controller
          control={control}
          name="value"
          render={({ field }) => (
            <Select
              aria-label={props.title}
              selectedKey={field.value}
              onSelectionChange={field.onChange}
              data-testid="listcontains-select"
              items={[
                { id: "true", label: props.yesLabel },
                { id: "false", label: props.noLabel },
              ]}
            >
              {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
            </Select>
          )}
        />
      </div>
    </Form>
  );
}
